import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormGroup } from '@angular/forms';
import html2canvas from 'html2canvas';
import { AgreementForm } from '../../shared-agreement.model';
import jsPDF from 'jspdf';
import { ApiClientService } from '@services/api-client';
import { CustomerResponse, GetUserResponse, GetUsersResponse } from '@services/api-client/responses';
import { CachedValuesService } from '@services/index';
import { map } from 'rxjs';
import { Counterpart } from 'app/types';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { toDateFromNgbDate, toDateISOString } from '@helpers/datetime-helper';
import { AgreementFormHelper } from '../../shared-agreement.helper';
import { APP_ROUTES } from 'app/constants/routes';
@UntilDestroy()
@Component({
    selector: 'app-agreement-summary',
    templateUrl: './agreement-summary.component.html',
    styleUrl: './agreement-summary.component.scss'
})
export class AgreementSummaryComponent implements OnInit {
    @Input() public agreementForm: FormGroup<AgreementForm>;
    @Input() public agreementCreated: string;
    @Output() public isCreatingPdf: EventEmitter<boolean> = new EventEmitter<boolean>();
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);

    public submitting: boolean;
    public generatingPdf: boolean;
    private printMode = false;

    public customer: CustomerResponse;
    public counterpart: Counterpart;
    public user: GetUserResponse;
    public users: GetUsersResponse;

    public get detailsForm() {
        return this.agreementForm.controls.details;
    }

    public get periodForm() {
        return this.agreementForm.controls.period;
    }

    public get reminderForm() {
        return this.agreementForm.controls.reminder;
    }

    public get paymentForm() {
        return this.agreementForm.controls.payment;
    }

    public get fileForm() {
        return this.agreementForm.controls.fileUpload;
    }

    public get f() {
        return this.agreementForm.controls;
    }

    public ngOnInit() {
        this.apiClientService.getCustomer$(this.f.details.value.customerId).subscribe({
            next: (response: CustomerResponse) => {
                this.customer = response;
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                console.log('Error fetching customer data');
            }
        });

        this.cachedValuesService.counterparts$
            .pipe(
                map((result) => {
                    return result.filter((p) => p.id == this.detailsForm.controls.counterpartId.value).at(0);
                })
            )
            .subscribe({
                next: (response: Counterpart) => {
                    this.changeDetectorRef.markForCheck();
                    this.counterpart = response;
                }
            });

        this.cachedValuesService.users$
            .pipe(
                map((result) => {
                    this.users = result;
                    return result.filter((p) => p.id == this.detailsForm.controls.responsibleId.value).at(0);
                })
            )
            .subscribe({
                next: (response: GetUserResponse) => {
                    this.changeDetectorRef.markForCheck();
                    this.user = response;
                }
            });
    }

    public get noticeDate(): Date | null {
        return AgreementFormHelper.getNoticeDate(this.periodForm.controls);
    }

    public get validToAfterExtension(): Date | null {
        return AgreementFormHelper.getValidToAfterExtension(this.periodForm.controls);
    }

    public goToReplacedAgreement() {
        const replacedByAgreementId = this.periodForm.controls.replacedAgreementId.value;
        if (replacedByAgreementId) {
            window.location.href = `/${APP_ROUTES.Agreement}/details/${replacedByAgreementId}`;
        }
    }

    public getDateString(date: NgbDate) {
        if (!date) return '';
        const d = toDateFromNgbDate(date);
        return toDateISOString(d);
    }

    public getStatus() {
        return this.f.details.controls.status.value;
    }

    public get reminderStartDate() {
        if (this.getReminderRecipients() === '') return '-';
        let startDate: Date;
        if (this.periodForm?.controls.validTo.value) {
            const validToNgbDate: NgbDate = this.periodForm.controls.validTo.value;
            startDate = toDateFromNgbDate(validToNgbDate);
            if (this.periodForm.controls.noticePeriodTypeId.value === 1) {
                startDate.setMonth(startDate.getMonth() - (this.periodForm.controls.noticePeriod.value ?? 0));
            } else {
                startDate.setDate(startDate.getDate() - (this.periodForm.controls.noticePeriod.value ?? 0));
            }
            startDate.setMonth(startDate.getMonth() - (this.reminderForm.controls.reminderPeriod.value ?? 0));
            return toDateISOString(startDate);
        }
        return '-';
    }

    public get numOfReminders() {
        if (this.getReminderRecipients() === '') return '-';
        return this.reminderForm.controls.numOfReminders.value > 0
            ? this.reminderForm.controls.numOfReminders.value.toString()
            : '-';
    }

    public getReminderRecipients() {
        if (this.reminderForm.controls.recipients.value?.length > 0 && this.users) {
            // Map recipient IDs to user full names
            return this.reminderForm.controls.recipients.value
                .map((id: string) => {
                    const user = this.users.find((u) => u.id === id);
                    return user ? `${user.firstName} ${user.lastName}` : id;
                })
                .join(', ');
        }
        return '';
    }

    public get lastDateForNotice(): string {
        return AgreementFormHelper.getlastDateForNoticeToString(this.periodForm.controls);
    }

    public download() {
        this.printMode = false;
        this.isCreatingPdf.emit(true);
        this.changeDetectorRef.markForCheck();
    }

    public printPDF() {
        this.printMode = true;
        this.isCreatingPdf.emit(true);
        this.changeDetectorRef.markForCheck();
    }

    public async downloadPDF() {
        const element = document.getElementById('print-content');
        if (!element) return;

        this.changeDetectorRef.markForCheck();
        // Show logo for PDF only
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const logo = element.querySelector('.pdf-logo') as HTMLElement;
        if (logo) logo.style.display = 'block';
        element.style.padding = '4rem';

        this.generatingPdf = true;
        this.changeDetectorRef.detectChanges();
        await Promise.resolve(); // Wait for DOM update
        await new Promise((resolve) => setTimeout(resolve, 100));

        html2canvas(element, { scale: 1 })
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');

                const imgWidth = 210; // A4 page width in mm
                const pageHeight = 297; // A4 page height in mm
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                let heightLeft = imgHeight;
                let position = 20;

                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;

                // Add new pages if content is longer than 1 page
                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }

                if (this.printMode) {
                    // Open PDF in new window and print
                    const pdfUrl = pdf.output('bloburl');
                    const printWindow = window.open(pdfUrl, '_blank');
                    if (printWindow) {
                        printWindow.onload = function () {
                            printWindow.focus();
                            printWindow.print();
                        };
                    }
                } else {
                    pdf.save('summary.pdf');
                }
            })
            .catch((error) => {
                console.error('Error generating PDF:', error);
            })
            .finally(() => {
                this.generatingPdf = false;
                this.isCreatingPdf.emit(false);
                if (logo) logo.style.display = 'none';
                element.style.padding = '0rem';
                this.changeDetectorRef.markForCheck();
            });
    }

    // public printSection(sectionId: string) {
    //     const printContent = document.getElementById(sectionId);
    //     if (!printContent) return;

    //     // Use the compiled CSS file for print styling
    //     const linkTag = '<link rel="stylesheet" type="text/css" href="/styles.css">';

    //     const windowPrt = window.open('', '', 'width=800,height=600');
    //     if (!windowPrt) return;

    //     windowPrt.document.write(`
    //   <html>
    //     <head>
    //       <title>Print</title>
    //       ${linkTag}
    //       <style>
    //         body { font-family: Arial, sans-serif; padding: 20px; }
    //       </style>
    //     </head>
    //     <body>
    //       ${printContent.innerHTML}
    //     </body>
    //   </html>
    // `);
    //     windowPrt.document.close();
    //     windowPrt.focus();
    //     windowPrt.print();
    //     windowPrt.close();
    // }
}
